import { useEffect, useRef, useState } from "react";
import { Box, IconButton, Menu, MenuItem, TextField } from "@mui/material";
import {
  MdContentCopy,
  MdDragIndicator,
  MdEdit,
  MdMoreHoriz,
  MdOutlineClose,
} from "react-icons/md";
import { formatCurrency } from "../../../../utils/format";

type PropTypes = {
  name: string;
  total: number;
  /** `false` no pseudo-agrupamento "Sem agrupamento": sem alça, sem menu. */
  isReal: boolean;
  /** Etapa somente-leitura: esconde alça e menu em vez de oferecer ação sem efeito. */
  readOnly: boolean;
  dragAttributes?: Record<string, any>;
  dragListeners?: Record<string, any>;
  onRename: (name: string) => void;
  onDuplicate: () => void;
  onDelete: () => void;
};

/**
 * Cabeçalho do agrupamento.
 *
 * A ordenação é por ARRASTO (alça à esquerda) — o menu tem apenas Renomear,
 * Duplicar e Excluir. "Mover para cima/baixo" foi descartado de propósito:
 * arrastar é o que a tela já faz nos outros três níveis (serviço, categoria e
 * fornecedor).
 */
const GroupHeader: React.FC<PropTypes> = ({
  name,
  total,
  isReal,
  readOnly,
  dragAttributes,
  dragListeners,
  onRename,
  onDuplicate,
  onDelete,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [draftName, setDraftName] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);
  // Esc fecha o campo, o que desmonta o input. Navegador nenhum dispara blur
  // ao remover o nó focado, mas depender disso deixaria o cancelamento
  // dependente do ambiente — e o custo de garantir é uma flag.
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (isRenaming) {
      // Texto pré-selecionado: renomear "Dia 1" para "Dia 2" é uma digitação só.
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isRenaming]);

  const startRename = () => {
    setDraftName(name);
    cancelledRef.current = false;
    setIsRenaming(true);
    setMenuAnchor(null);
  };

  const cancelRename = () => {
    cancelledRef.current = true;
    setIsRenaming(false);
  };

  const commitRename = () => {
    if (cancelledRef.current) return;
    const trimmed = draftName.trim();
    setIsRenaming(false);
    // Nome vazio ou inalterado: descarta em silêncio em vez de gravar lixo.
    if (trimmed && trimmed !== name) onRename(trimmed);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 1,
        borderLeft: "4px solid #1361A4",
        borderRadius: "6px",
        backgroundColor: "#F2F6FB",
        minHeight: 44,
      }}
    >
      {isReal && !readOnly && (
        <Box
          {...dragAttributes}
          {...dragListeners}
          aria-label={`Reordenar agrupamento ${name}`}
          sx={{
            cursor: "grab",
            "&:active": { cursor: "grabbing" },
            display: "flex",
            alignItems: "center",
            p: "2px",
            "&:hover": { backgroundColor: "#E3ECF7", borderRadius: 1 },
          }}
        >
          <MdDragIndicator size={20} color="#666" />
        </Box>
      )}

      <Box
        component="span"
        sx={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.5px",
          color: "#1361A4",
          backgroundColor: "#E6F1FB",
          border: "1px solid #C7DDF3",
          borderRadius: "4px",
          px: 0.75,
          py: "2px",
          whiteSpace: "nowrap",
        }}
      >
        AGRUPAMENTO
      </Box>

      {isRenaming ? (
        <TextField
          inputRef={inputRef}
          value={draftName}
          size="small"
          fullWidth
          onChange={(event) => setDraftName(event.target.value)}
          onBlur={commitRename}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commitRename();
            }
            if (event.key === "Escape") {
              event.preventDefault();
              cancelRename();
            }
          }}
          inputProps={{ "aria-label": "Nome do agrupamento" }}
          sx={{ backgroundColor: "#FFF", maxWidth: 420 }}
        />
      ) : (
        <Box
          component="span"
          sx={{
            fontSize: 15,
            fontWeight: 600,
            color: isReal ? "#333" : "#777",
            flex: 1,
          }}
        >
          {name}
        </Box>
      )}

      <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
        <Box
          component="span"
          sx={{ fontSize: 14, fontWeight: 600, color: "#333" }}
        >
          {formatCurrency(total)}
        </Box>

        {isReal && !readOnly && (
          <>
            <IconButton
              size="small"
              aria-label={`Ações do agrupamento ${name}`}
              onClick={(event) => setMenuAnchor(event.currentTarget)}
            >
              <MdMoreHoriz size={18} color="#666" />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={!!menuAnchor}
              onClose={() => setMenuAnchor(null)}
            >
              <MenuItem onClick={startRename} sx={{ gap: 1, fontSize: 14 }}>
                <MdEdit size={16} /> Renomear
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  onDuplicate();
                }}
                sx={{ gap: 1, fontSize: 14 }}
              >
                <MdContentCopy size={16} /> Duplicar
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  onDelete();
                }}
                sx={{ gap: 1, fontSize: 14, color: "#C62828" }}
              >
                <MdOutlineClose size={16} /> Excluir
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>
    </Box>
  );
};

export default GroupHeader;
